// CooperativaWizardModal — registro / edición de una cooperativa en
// CoopNet. Portado del asistente de Fenagro:
//   1. Datos generales
//   2. Datos contrato (NIT, representante legal, cédula, cuenta bancaria)
//   3. Documentos (RUT + cédula del representante, con OCR)
//   4. Ubicación (país / departamento / municipio, dirección, lat/lng)
//
// DIFERENCIA CON FENAGRO (a propósito): allá "Documentos" va ANTES de
// "Datos contrato", así que al subir el RUT el NIT todavía no está
// digitado y el servicio solo lo cruza por nombre. Aquí el contrato va
// primero para que el RUT se cruce con el NIT y la razón social, y la
// cédula con el nombre y el número del representante. Es solo el orden
// del arreglo PASOS y los índices de PASO: se revierte cambiándolo.
//
// Documentos: se verifican y almacenan con el servicio de Ecolab/Fenagro
// (TRANSITORIO — ver services/ecolabDocumentos.js). Reglas de Fenagro:
// RUT obligatorio al crear; la cédula del representante se verifica o se
// marca como pendiente (aprobación posterior). Si cambian los datos con
// los que se cruzó un documento, se invalida y hay que subirlo de nuevo.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { organizacionApi } from '../services/coopnetApi';
import SelectorGeografia from './SelectorGeografia';
import VerificadorDocumento from './VerificadorDocumento';

const PASOS = ['Datos generales', 'Datos contrato', 'Documentos', 'Ubicación'];
const PASO = { GENERAL: 0, CONTRATO: 1, DOCUMENTOS: 2, UBICACION: 3 };
const PAIS_COLOMBIA = 1045;

const FORM_INICIAL = {
  nombre: '',
  siglas: '',
  descripcion: '',
  email: '',
  telefono: '',
  sitioWeb: '',
  fechaConstitucion: '',
  nit: '',
  representanteLegal: '',
  cedulaRepresentante: '',
  cuentaBancaria: '',
  paisId: PAIS_COLOMBIA,
  departamentoId: '',
  municipioId: '',
  direccion: '',
  latitud: '',
  longitud: '',
};

const aTexto = (v) => (v === null || v === undefined ? '' : String(v));
const aEntero = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));
const aDecimal = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));

const ETIQUETA_DOCS = {
  COMPLETO: 'Documentos completos',
  PENDIENTE_CEDULA_REP: 'Pendiente: cédula del representante',
  PENDIENTE_DOCUMENTOS: 'Documentos pendientes',
};

const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };

/**
 * @param {boolean} open
 * @param {'crear'|'editar'} modo
 * @param {string} federacionId  federación donde se registra (solo modo crear)
 * @param {object|null} cooperativa  cooperativa a editar (modo editar)
 * @param {() => void} onClose
 * @param {(cooperativa: object) => void} onGuardado
 */
export default function CooperativaWizardModal({ open, modo = 'crear', federacionId, cooperativa = null, onClose, onGuardado }) {
  const editar = modo === 'editar';
  const [paso, setPaso] = useState(0);
  const [form, setForm] = useState(FORM_INICIAL);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [geoCompleta, setGeoCompleta] = useState(false); // país/departamento/municipio ya elegidos (lo informa SelectorGeografia)

  // Documentos verificados ({ url, nombre }) y los datos con que se cruzaron.
  const [docs, setDocs] = useState({ rut: null, cedula: null });
  const [snapRut, setSnapRut] = useState(null); // { nombre, nit }
  const [snapCed, setSnapCed] = useState(null); // { representante, cedula }
  const [cedulaPendiente, setCedulaPendiente] = useState(false);
  const [avisoInvalidados, setAvisoInvalidados] = useState(false);
  const [resetRut, setResetRut] = useState(0);
  const [resetCed, setResetCed] = useState(0);

  useEffect(() => {
    if (!open) return;
    setPaso(0);
    setError('');
    setDocs({ rut: null, cedula: null });
    setSnapRut(null);
    setSnapCed(null);
    setCedulaPendiente(false);
    setAvisoInvalidados(false);
    if (cooperativa) {
      setForm({
        ...FORM_INICIAL,
        nombre: aTexto(cooperativa.nombre),
        siglas: aTexto(cooperativa.siglas),
        descripcion: aTexto(cooperativa.descripcion),
        email: aTexto(cooperativa.email),
        telefono: aTexto(cooperativa.telefono),
        sitioWeb: aTexto(cooperativa.sitioWeb),
        fechaConstitucion: aTexto(cooperativa.fechaConstitucion),
        nit: aTexto(cooperativa.nit),
        representanteLegal: aTexto(cooperativa.representanteLegal),
        cedulaRepresentante: aTexto(cooperativa.cedulaRepresentante),
        cuentaBancaria: aTexto(cooperativa.cuentaBancaria),
        paisId: cooperativa.paisId ?? PAIS_COLOMBIA,
        departamentoId: aTexto(cooperativa.departamentoId),
        municipioId: aTexto(cooperativa.municipioId),
        direccion: aTexto(cooperativa.direccion),
        latitud: aTexto(cooperativa.latitud),
        longitud: aTexto(cooperativa.longitud),
      });
    } else {
      setForm(FORM_INICIAL);
    }
  }, [open, cooperativa]);

  // Si cambian los datos con los que se cruzó un documento, se invalida.
  useEffect(() => {
    if (!docs.rut || !snapRut) return;
    if (form.nombre.trim() !== snapRut.nombre || form.nit.trim() !== snapRut.nit) {
      setDocs((d) => ({ ...d, rut: null }));
      setSnapRut(null);
      setResetRut((n) => n + 1);
      setAvisoInvalidados(true);
    }
  }, [form.nombre, form.nit, docs.rut, snapRut]);

  useEffect(() => {
    if (!docs.cedula || !snapCed) return;
    if (form.representanteLegal.trim() !== snapCed.representante || form.cedulaRepresentante.trim() !== snapCed.cedula) {
      setDocs((d) => ({ ...d, cedula: null }));
      setSnapCed(null);
      setResetCed((n) => n + 1);
      setAvisoInvalidados(true);
    }
  }, [form.representanteLegal, form.cedulaRepresentante, docs.cedula, snapCed]);

  const set = useCallback((campo, valor) => setForm((f) => ({ ...f, [campo]: valor })), []);

  const puedeAvanzar = useMemo(() => {
    if (paso === PASO.GENERAL) return Boolean(form.nombre.trim() && form.email.trim());
    if (paso === PASO.CONTRATO) return Boolean(form.nit.trim() && form.representanteLegal.trim() && form.cedulaRepresentante.trim());
    // Al crear: RUT obligatorio y cédula verificada o marcada pendiente.
    // Al editar los documentos son opcionales (se pueden adjuntar después).
    if (paso === PASO.DOCUMENTOS) return editar || Boolean(docs.rut && (docs.cedula || cedulaPendiente));
    if (paso === PASO.UBICACION) return geoCompleta;
    return false;
  }, [paso, form, docs, cedulaPendiente, editar, geoCompleta]);

  const marcarCedulaPendiente = () => {
    const ok = window.confirm(
      '¿Marcar la cédula del representante como pendiente?\n\n' +
        'La cooperativa se registrará, pero quedará sujeta a aprobación: un SuperAdmin o un AdminFederacion ' +
        'deberá adjuntar la cédula después. Recuerda avisarles (CoopNet aún no envía notificaciones automáticas).'
    );
    if (ok) {
      setCedulaPendiente(true);
      setSnapCed(null);
      setDocs((d) => ({ ...d, cedula: null }));
      setResetCed((n) => n + 1);
    }
  };

  const guardar = async () => {
    setEnviando(true);
    setError('');
    try {
      const urlCedula = cedulaPendiente ? null : docs.cedula?.url || null;
      const dto = {
        nombre: form.nombre,
        siglas: form.siglas,
        descripcion: form.descripcion,
        email: form.email,
        telefono: form.telefono,
        sitioWeb: form.sitioWeb,
        fechaConstitucion: form.fechaConstitucion || null,
        nit: form.nit,
        representanteLegal: form.representanteLegal,
        cedulaRepresentante: form.cedulaRepresentante,
        cuentaBancaria: form.cuentaBancaria,
        paisId: aEntero(form.paisId),
        departamentoId: aEntero(form.departamentoId),
        municipioId: aEntero(form.municipioId),
        direccion: form.direccion,
        latitud: aDecimal(form.latitud),
        longitud: aDecimal(form.longitud),
        urlRut: docs.rut?.url || null,
        urlCedulaRepresentante: urlCedula,
        cedulaRepPendiente: cedulaPendiente,
      };

      if (!editar) {
        const creada = await organizacionApi.crearCooperativa(federacionId, dto);
        onGuardado?.(creada);
        onClose?.();
        return;
      }

      // Editar: el PUT no toca documentos; los recién verificados se adjuntan aparte.
      const actualizada = await organizacionApi.actualizarCooperativa(cooperativa.id, dto);
      const errores = [];
      const nuevos = [
        ['RUT', docs.rut],
        ['CEDULA_REPRESENTANTE', cedulaPendiente ? null : docs.cedula],
      ];
      for (const [tipoDocumento, doc] of nuevos) {
        if (!doc) continue;
        try {
          await organizacionApi.registrarDocumentoCooperativa(cooperativa.id, { tipoDocumento, url: doc.url, nombreArchivo: doc.nombre });
        } catch (e) {
          errores.push(`${tipoDocumento}: ${e.message}`);
        }
      }
      onGuardado?.(actualizada);
      if (errores.length) {
        setError(`Los datos se guardaron, pero no se pudieron adjuntar documentos — ${errores.join(' | ')}`);
        return;
      }
      onClose?.();
    } catch (e) {
      setError(e?.message || 'No se pudo guardar la cooperativa.');
    } finally {
      setEnviando(false);
    }
  };

  const campo = (label, nombre, extra = {}) => (
    <TextField label={label} value={form[nombre]} onChange={(e) => set(nombre, e.target.value)} fullWidth {...extra} />
  );

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {editar ? 'Editar cooperativa' : 'Nueva cooperativa'}
        <Typography variant="body2" color="text.secondary">
          Datos generales, contrato, documentos y ubicación
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={paso} alternativeLabel sx={{ mb: 3 }}>
          {PASOS.map((p) => (
            <Step key={p}>
              <StepLabel>{p}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {paso === PASO.GENERAL && (
          <Box sx={grid}>
            {campo('Nombre de la cooperativa *', 'nombre', { autoFocus: true })}
            {campo('Siglas', 'siglas')}
            {campo('Correo electrónico *', 'email', { type: 'email' })}
            {campo('Teléfono', 'telefono')}
            {campo('Sitio web', 'sitioWeb')}
            {campo('Fecha de constitución', 'fechaConstitucion', { type: 'date', InputLabelProps: { shrink: true } })}
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>{campo('Descripción', 'descripcion', { multiline: true, minRows: 2 })}</Box>
          </Box>
        )}

        {paso === PASO.CONTRATO && (
          <Box sx={grid}>
            {campo('NIT *', 'nit')}
            {campo('Representante legal *', 'representanteLegal')}
            {campo('Cédula del representante *', 'cedulaRepresentante')}
            {campo('Cuenta bancaria', 'cuentaBancaria')}
          </Box>
        )}

        {paso === PASO.DOCUMENTOS && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Verificamos que los datos coincidan con los documentos oficiales.
              {editar ? ' Al editar, subir documentos es opcional.' : ''}
            </Typography>

            {editar && cooperativa && (
              <Box>
                <Chip
                  color={cooperativa.estadoDocumentos === 'COMPLETO' ? 'success' : 'warning'}
                  label={`Estado actual: ${ETIQUETA_DOCS[cooperativa.estadoDocumentos] || cooperativa.estadoDocumentos}`}
                />
              </Box>
            )}

            {avisoInvalidados && (
              <Alert severity="warning" onClose={() => setAvisoInvalidados(false)}>
                Por seguridad debe volver a subir el documento: cambió algún dato con el que se verificó.
              </Alert>
            )}

            <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 700 }}>RUT de la cooperativa</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                RUT de <b>{form.nombre}</b> (NIT <b>{form.nit}</b>). El NIT y el nombre deben aparecer en el documento.
              </Typography>
              <VerificadorDocumento
                key={`rut-${resetRut}`}
                descripcion="Formatos aceptados: PDF, JPG, PNG. Máximo 10 MB."
                tipoDocumento="rut"
                numero={form.nit}
                nombres={form.nombre}
                onValido={(file, res) => {
                  setDocs((d) => ({ ...d, rut: { url: res.urlDocumento, nombre: file.name } }));
                  setSnapRut({ nombre: form.nombre.trim(), nit: form.nit.trim() });
                  setAvisoInvalidados(false);
                }}
                onInvalido={() => {
                  setDocs((d) => ({ ...d, rut: null }));
                  setSnapRut(null);
                }}
              />
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: cedulaPendiente ? '2px dashed' : '1px solid',
                borderColor: cedulaPendiente ? 'warning.main' : 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700 }}>Cédula del representante legal (ambas caras)</Typography>
                {!cedulaPendiente && !docs.cedula && (
                  <Button size="small" variant="outlined" color="warning" onClick={marcarCedulaPendiente}>
                    Marcar como pendiente
                  </Button>
                )}
                {cedulaPendiente && (
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => {
                      setCedulaPendiente(false);
                      setResetCed((n) => n + 1);
                    }}
                  >
                    Deshacer (subir ahora)
                  </Button>
                )}
              </Box>

              {cedulaPendiente ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <b>Cédula marcada como pendiente.</b> La cooperativa se creará, pero quedará sujeta a aprobación: un
                  SuperAdmin o un AdminFederacion deberá adjuntar la cédula después.
                </Alert>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Suba la cédula escaneada por <b>ambas caras (frente y reverso) en un solo archivo PDF</b>.
                  </Alert>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Cédula de <b>{form.representanteLegal}</b> (CC <b>{form.cedulaRepresentante}</b>). Los datos deben
                    coincidir exactamente.
                  </Typography>
                  <VerificadorDocumento
                    key={`ced-${resetCed}`}
                    descripcion="Formatos aceptados: PDF, JPG, PNG. Máximo 10 MB."
                    tipoDocumento="cedula"
                    numero={form.cedulaRepresentante}
                    nombres={form.representanteLegal}
                    onValido={(file, res) => {
                      setDocs((d) => ({ ...d, cedula: { url: res.urlDocumento, nombre: file.name } }));
                      setSnapCed({ representante: form.representanteLegal.trim(), cedula: form.cedulaRepresentante.trim() });
                      setAvisoInvalidados(false);
                    }}
                    onInvalido={() => {
                      setDocs((d) => ({ ...d, cedula: null }));
                      setSnapCed(null);
                    }}
                  />
                </>
              )}
            </Box>
          </Box>
        )}

        {paso === PASO.UBICACION && (
          <Box sx={grid}>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <SelectorGeografia
                nivel="municipio"
                valor={{ paisId: form.paisId, departamentoId: form.departamentoId, municipioId: form.municipioId }}
                onChange={(g) =>
                  setForm((f) => ({ ...f, paisId: g.paisId, departamentoId: g.departamentoId, municipioId: g.municipioId }))
                }
                onCompletoChange={setGeoCompleta}
                etiquetas={{ pais: 'País *', municipio: 'Municipio *' }}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>{campo('Dirección', 'direccion')}</Box>
            {campo('Latitud', 'latitud', { type: 'number', helperText: 'Opcional. Más adelante se podrá elegir en un mapa.' })}
            {campo('Longitud', 'longitud', { type: 'number' })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={enviando}>
          Cancelar
        </Button>
        <Box sx={{ flex: 1 }} />
        {paso > 0 && (
          <Button onClick={() => setPaso((p) => p - 1)} disabled={enviando}>
            Atrás
          </Button>
        )}
        {paso < PASOS.length - 1 ? (
          <Button variant="contained" onClick={() => setPaso((p) => p + 1)} disabled={!puedeAvanzar}>
            Siguiente
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={guardar}
            disabled={!puedeAvanzar || enviando}
            startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {editar ? 'Guardar cambios' : 'Registrar cooperativa'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
